import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, FormHelperText, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridCellEditCommitParams, GridCellModes, GridCellModesModel, GridCellParams } from '@mui/x-data-grid';
import { updateQuoteRegions } from '../../helper/formatQuoteForUpdate';
import { clamp, formatCurrency } from '../../helper/constants';
import { regions } from '../../helper/regions';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import { Quote, QuoteRatingRegion } from '../../shared/types/quotes';
import { map } from 'lodash';
import { colours } from '../../shared/colors';
import { regionDataRowsToQuoteRatingRegions } from '../../shared/types/regionData';
import { useUpdateQuoteGroupMutation } from '../../services/features/quoteApi/quoteApi';
import DataGridNumericInput from '../DataGridNumericInput';

export const RatingRegionsTable = ({
	error,
	setFieldTouched,
}: {
	error: boolean;
	setFieldTouched: Function;
}) => {
	// const [errors, setErrors] = useState<string[]>([]);
	const [selectedCell, setSelectedCell] = useState<null | string>(null);

	const {
		quoteGroup,
		currentQuotes,
		loadingProducts,
		setLoadingProducts,
		ratingRegionsRows,
		setRatingRegionsRows,
		isReferred,
		isDeclined,
		isReferralApproved,
		mapQuotesToRows,
		calculateTotalsAndPercentages,
	} = useQuoteContext();

	const disabled = isReferred || isReferralApproved || isDeclined;

	const [updateQuoteGroup] = useUpdateQuoteGroupMutation();

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: '',
			editable: false,
			resizable: false,
			sortable: false,
			flex: 1,
		},
		{
			field: 'lastYearAmount',
			headerName: 'Last Financial Year Turnover (Actual) $',
			editable: true,
			resizable: false,
			sortable: false,
			align: 'right',
			flex: 1,
			renderCell: (params) => (
				<span style={{ ...(disabled && { color: 'rgba(0,0,0,0.5)' }) }}>{`${
					formatCurrency(params.row[params.field]).split('.')[0]
				}`}</span>
			),
			renderEditCell: (params) => (
				<DataGridNumericInput {...params} isCurrency={true} decimalScale={0} />
			),
		},
		{
			field: 'currentYearAmount',
			headerName: 'Current Financial Year Turnover (Projected) $',
			editable: true,
			resizable: false,
			sortable: false,
			align: 'right',
			flex: 1,
			renderCell: (params) => (
				<span style={{ ...(disabled && { color: 'rgba(0,0,0,0.5)' }) }}>{`${
					formatCurrency(params.row[params.field]).split('.')[0]
				}`}</span>
			),
			renderEditCell: (params) => (
				<DataGridNumericInput {...params} isCurrency={true} decimalScale={0} />
			),
		},
		{
			field: 'staffNumbers',
			headerName: 'Number of employees (incl. Directors)',
			editable: true,
			resizable: false,
			sortable: false,
			align: 'right',
			flex: 1,
			renderCell: (params) => (
				<span style={{ ...(disabled && { color: 'rgba(0,0,0,0.5)' }) }}>
					{params.row[params.field]}
				</span>
			),
			renderEditCell: (params) => (
				<DataGridNumericInput {...params} isNumber={true} decimalScale={0} />
			),
		},
	];

	const handleEditCellCommit = (params: GridCellEditCommitParams) => {
		const { id, field, value } = params;
		const newStringValue = `${value}`.slice(0, 12);
		setRatingRegionsRows((prevRows) => {
			const numericValue = isNaN(Number(newStringValue)) ? 0 : Number(newStringValue);
			const newValue =
				numericValue > Number.MAX_SAFE_INTEGER ? Number.MAX_SAFE_INTEGER : numericValue;

			const updatedRows = prevRows.map((row) =>
				row.id === id
					? { ...row, [field]: newValue } //Math.max(0, isNaN(Number(value)) ? 0 : Number(value)) }
					: row
			);
			return calculateTotalsAndPercentages(updatedRows);
		});
	};

	const currentRegions = useMemo(() => {
		let currentRegions = currentQuotes.flatMap((q) => q.quoteRatingRegions);
		return currentRegions.filter(
			(v, i) =>
				currentRegions.findIndex((item) => item.ratingRegionId === v.ratingRegionId) === i
		);
	}, [currentQuotes]);

	const convertRowsToQuoteRatingRegions = useCallback((): QuoteRatingRegion[] => {
		const currentRegionNames = currentRegions.map((r) => r.ratingRegionName);

		const updatedRegions = currentRegions.map((region) => {
			const row = ratingRegionsRows.find((r) => r.name === region.ratingRegionName);
			const lastYearAmount = Number(row?.lastYearAmount ?? 0);
			const currentYearAmount = Number(row?.currentYearAmount ?? 0);
			const staffNumbers = Number(row?.staffNumbers ?? 0);
			return {
				...region,
				...(row && {
					lastYearAmount,
					currentYearAmount,
					staffNumbers,
					turnoverPercentageCurrentYear: null,
					turnoverPercentageLastYear: null,
				}),
			};
		});
		const newRegions = regionDataRowsToQuoteRatingRegions(ratingRegionsRows).filter(
			(r) => !currentRegionNames.includes(r.ratingRegionName)
		);
		return updatedRegions.concat(newRegions);
	}, [ratingRegionsRows, currentRegions]);

	const regionIsDifferent = (region1: QuoteRatingRegion, region2: QuoteRatingRegion) => {
		return (
			region1.ratingRegionId === region2.ratingRegionId &&
			(region1.staffNumbers !== region2.staffNumbers ||
				region1.currentYearAmount !== region2.currentYearAmount ||
				region1.lastYearAmount !== region2.lastYearAmount)
		);
	};

	const quoteRatingRegions = useMemo(() => {
		const updatedRegions = convertRowsToQuoteRatingRegions();
		return updatedRegions.filter((r) => {
			const existing = currentRegions.find((reg) => reg.ratingRegionId === r.ratingRegionId);
			return (
				(existing && regionIsDifferent(existing, r)) ||
				r.staffNumbers !== 0 ||
				r.currentYearAmount !== 0 ||
				r.lastYearAmount !== 0
			);
		});
	}, [currentRegions, convertRowsToQuoteRatingRegions]);

	const shouldUpdate = (currentRegions: QuoteRatingRegion[], newRegions: QuoteRatingRegion[]) => {
		if (currentQuotes.length === 0) {
			return false;
		}

		const currentRegionIds = currentRegions.map((r) => r.ratingRegionId);

		if (
			newRegions.every(
				(reg) =>
					reg.staffNumbers === 0 &&
					reg.currentYearAmount === 0 &&
					reg.lastYearAmount === 0
			)
		) {
			return false;
		}
		if (newRegions.some((reg) => !currentRegionIds.includes(reg.ratingRegionId))) {
			return true;
		}
		if (newRegions.some((reg) => currentRegions.some((r) => regionIsDifferent(reg, r)))) {
			return true;
		}
		return false;
	};

	useEffect(() => {
		const updateRegions = async () => {
			if (quoteGroup) {
				setLoadingProducts(true);
				const update = updateQuoteRegions(quoteGroup, quoteRatingRegions);
				await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
					.unwrap()
					.catch((error) => {
						console.log('Error in updating quote regions', error);
						mapQuotesToRows(currentQuotes);
					});
				setLoadingProducts(false);
			}
		};
		const needsUpdate = shouldUpdate(currentRegions, quoteRatingRegions);
		if (needsUpdate && selectedCell === null && !loadingProducts) {
			// console.log("UPDATE REGIONS", quoteRatingRegions, "CURRENT", currentRegions);
			updateRegions();
		}
	}, [quoteGroup, loadingProducts, quoteRatingRegions, currentRegions, selectedCell]);

	const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});

	const handleCellClick = useCallback((params: GridCellParams, event: React.MouseEvent) => {
		if (!params.isEditable) {
			return;
		}

		// Ignore portal
		if (
			(event.target as any).nodeType === 1 &&
			!event.currentTarget.contains(event.target as Element)
		) {
			return;
		}

        const cellId = `${params.field}-${params.id}`;
		setSelectedCell(cellId);

		setCellModesModel((prevModel) => {
			return {
				// Revert the mode of the other cells from other rows
				...Object.keys(prevModel).reduce(
					(acc, id) => ({
						...acc,
						[id]: Object.keys(prevModel[id]).reduce(
							(acc2, field) => ({
								...acc2,
								[field]: { mode: GridCellModes.View },
							}),
							{}
						),
					}),
					{}
				),
				[params.id]: {
					// Revert the mode of other cells in the same row
					...Object.keys(prevModel[params.id] || {}).reduce(
						(acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
						{}
					),
					[params.field]: { mode: GridCellModes.Edit },
				},
			};
		});
	}, []);

	const handleCellModesModelChange = useCallback((newModel: GridCellModesModel) => {
		setCellModesModel(newModel);
	}, []);

	return (
		<Box width='100%' py='1em'>
			<DataGrid
				loading={loadingProducts}
				rows={ratingRegionsRows}
				columns={columns}
				onCellEditCommit={handleEditCellCommit}
				getRowClassName={(_) => (error ? 'rowWithError' : '')}
				disableSelectionOnClick
				hideFooter
				hideFooterPagination
				disableColumnMenu
				hideFooterSelectedRowCount
				showCellRightBorder
				isCellEditable={(params) => !disabled && params.row.name.toLowerCase() !== 'total'}
				cellModesModel={cellModesModel}
                onCellModesModelChange={handleCellModesModelChange}
                onCellClick={handleCellClick}
                // onCellClick={(params) => {
				// 	const cellId = `${params.field}-${params.id}`;
				// 	setSelectedCell(cellId);
				// }}
				onCellFocusOut={(params) => {
					const cellId = `${params.field}-${params.id}`;
					setFieldTouched();
					if (selectedCell === cellId) {
						setSelectedCell(null);
					}
				}}
				sx={{
					height: 580,
					'& .MuiDataGrid-columnHeaderTitle': {
						whiteSpace: 'normal',
						lineHeight: 'normal',
						fontWeight: '900',
						fontFamily: 'Poppins-Regular',
					},
					'& .MuiDataGrid-columnHeader': {
						height: 'unset !important',
						// minWidth: "25% !important",
					},
					'& .MuiDataGrid-columnHeaders': {
						maxHeight: '168px !important',
						backgroundColor: '#f9f9f9',
						lineHeight: '56px',
					},
					...(error && {
						'& .MuiDataGrid-main': {
							borderRadius: '4px',
							border: '1px solid #d32f2f',
						},
					}),
				}}
			/>
		</Box>
	);
};
